import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../hooks/useNotifications';
import { useAuth } from '../hooks/useAuth';
import { isVideoRecorder } from '../utils/roles';

function formatRelativeTime(iso: string): string {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const recorderView = isVideoRecorder(user?.role);
  const { data, isLoading } = useNotifications(true);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const items = data?.items ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleItemClick = (id: string, isUnread: boolean) => {
    if (isUnread) markRead.mutate(id);
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        className="relative rounded-md p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-80 sm:w-96 rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Notifications</h2>
            {unreadCount > 0 ? (
              <button
                type="button"
                className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isLoading}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            ) : null}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">No notifications yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {items.map((n) => {
                  const isUnread = !n.readAtUtc;
                  return (
                    <li key={n.id}>
                      <Link
                        to={recorderView ? `/my-tickets` : `/tickets/${n.ticketId}`}
                        className={`block px-4 py-3 hover:bg-gray-50 transition-colors ${
                          isUnread ? 'bg-primary-50/50' : ''
                        }`}
                        onClick={() => handleItemClick(n.id, isUnread)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-gray-900">{n.title}</p>
                          {isUnread ? (
                            <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" />
                          ) : null}
                        </div>
                        <p className="mt-0.5 text-xs text-gray-600 line-clamp-2">{n.message}</p>
                        <p className="mt-1 text-xs text-gray-400">{formatRelativeTime(n.createdAtUtc)}</p>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {unreadCount > 0 ? (
            <div className="border-t border-gray-100 px-4 py-2">
              <Link
                to={recorderView ? '/my-tickets' : '/tickets?status=completed'}
                className="text-xs font-medium text-primary-600 hover:text-primary-800"
                onClick={() => setOpen(false)}
              >
                {recorderView ? 'View my tickets →' : 'View ready-to-send tickets →'}
              </Link>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default NotificationBell;
